import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, message, Modal, Spin } from 'antd'
import { DefaultJsonForm } from '../../../../../../components/v2/default-json-form'
import { schema } from './schema'
import { convertData2JsonForm } from '../../../../../../shared/lib/convertData2JsonForm'
import fetchAPI from '../../../../../../../lib/utils/fetch-api'
import { isFormDataRequired } from '../../../../../../../lib/utils/collections'
import dayjs from 'dayjs'
import { IFormData, ISchemaActual, MeasurementModalProps, TimeResponse } from './interfaces'

export const MeasurementModal: React.FC<MeasurementModalProps> = memo(
  (props: MeasurementModalProps) => {
    const { id, isModalOpen, onOk, onCancel } = props

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [enums, setEnums] = useState<any>({})
    const [customers, setCustomers] = useState<any>({})
    const [loadingTimeOptions, setLoadingTimeOptions] = useState(false)
    const [formData, setFormData] = useState<IFormData>({
      measurement_responsible: undefined,
      measurement_date: undefined,
      measurement_time: undefined,
      measurement_scan: undefined,
      comment: undefined,
      send_tg_notification: false,
    })
    const [disabledDates, setDisabledDates] = useState<string[]>([])
    const [availableTimes, setAvailableTimes] = useState<any>(null)
    const [defaultTimes, setDefaultTimes] = useState<any>(null)
    const [responsible, setResponsible] = useState<null | number>(null)

    const fetchData = useCallback(async () => {
      setLoading(true)
      try {
        const res = await fetchAPI(`/api/order/${id}/measurement`)
        setEnums(res.enums)
        setCustomers(res.customers)
      } catch (error) {
        message.error('Ошибка при загрузке данных')
        setError('Ошибка при загрузке данных')
      } finally {
        setLoading(false)
      }
    }, [id])

    const fetchAvailableTimes = useCallback(
      async (responsible: number) => {
        setLoadingTimeOptions(true)
        try {
          const response = (await fetchAPI('/api/measurement-time', {
            method: 'POST',
            body: JSON.stringify({ responsible }),
          })) as TimeResponse
          setAvailableTimes(response?.timeOptions || [])
          setDefaultTimes(response?.defaultTimes || [])
          setDisabledDates(response.disabledDates)
        } catch (error) {
          message.error('Не удалось загрузить доступное время')
          console.error('Ошибка при загрузке времени:', error)
        } finally {
          setLoadingTimeOptions(false)
        }
      },
      [formData.measurement_date],
    )

    useEffect(() => {
      if (isModalOpen) {
        fetchData()
      }
    }, [isModalOpen, fetchData])

    // Сброс measurement_time при изменении measurement_date
    useEffect(() => {
      if (!formData.measurement_date) {
        setFormData(prev => ({ ...prev, measurement_time: undefined }))
      }
    }, [formData.measurement_date])

    const handleOk = useCallback(() => {
      const isRequiredFields = isFormDataRequired(formData, actualSchema)
      if (!isRequiredFields) {
        Modal.warning({
          title: 'Предупреждение',
          content: 'Заполните все обязательные поля',
        })
        return
      }
      onOk?.(formData)
    }, [formData, onOk])

    const handleCancel = (e: React.MouseEvent<HTMLElement>) => {
      setFormData({
        measurement_responsible: undefined,
        measurement_date: undefined,
        measurement_time: undefined,
        measurement_scan: undefined,
        comment: undefined,
        send_tg_notification: false,
      })
      setError(null)
      setAvailableTimes(null)
      setDefaultTimes(null)
      setDisabledDates([])
      setLoadingTimeOptions(false)
      setResponsible(null)
      onCancel?.(e)
    }

    const onChange = useCallback(
      data => {
        if (!data.formData.measurement_date) {
          setFormData(prevData => ({
            ...prevData,
            ...data.formData,
            measurement_time: undefined,
          }))
        }
        if (data.formData.measurement_responsible !== responsible) {
          setFormData(prevData => ({
            ...prevData,
            ...data.formData,
            measurement_date: undefined,
            measurement_time: undefined,
          }))
          setResponsible(data.formData.measurement_responsible)
          fetchAvailableTimes(data.formData.measurement_responsible)
        } else {
          setFormData(prevData => ({
            ...prevData,
            ...data.formData,
          }))
        }
      },
      [setFormData, responsible, setResponsible, fetchAvailableTimes],
    )

    const actualSchema = useMemo(() => {
      const schemaActual = (convertData2JsonForm(schema.fields) as never) as ISchemaActual
      const { measurement_responsible, measurement_date } = formData

      if (schemaActual.resultSchema && schemaActual.resultSchema.properties) {
        if (enums.managers) {
          schemaActual.resultSchema.properties.measurement_responsible.oneOf = enums.managers.map(
            (manager: any) => ({
              const: manager.id,
              title: manager.value,
            }),
          )
          schemaActual.resultSchema.properties.measurement_responsible.enum = schemaActual.resultSchema.properties.measurement_responsible.oneOf.reduce(
            (acc, curr) => {
              acc.push(curr.const)
              return acc
            },
            [],
          )
        }

        // Настраиваем календарь с отключенными датами
        schemaActual.resultSchema.properties.measurement_date = {
          ...schemaActual.resultSchema.properties.measurement_date,
          options: {
            ...schemaActual.resultSchema.properties.measurement_date?.options,
            disabled: !Boolean(measurement_responsible) || loadingTimeOptions,
            disabledDate: disabledDates,
            placeholder: loadingTimeOptions ? 'Загрузка' : 'Дата замера',
          },
        }

        // Обновляем опции для доступного времени
        if (measurement_date) {
          // Находим timeOptions для выбранной даты
          const dateStr = dayjs(measurement_date).format('YYYY-MM-DD').toString()
          const timeOptions = availableTimes.find(i => i.date === dateStr)?.times || defaultTimes
          schemaActual.resultSchema.properties.measurement_time.oneOf = timeOptions.map(option => ({
            const: option.value,
            title: option.value,
            disabled: option.disabled,
          }))
          schemaActual.resultSchema.properties.measurement_time.enum = timeOptions.map(
            item => item.value,
          )
          schemaActual.resultSchema.properties.measurement_time.options.allowSelectAll =
            timeOptions.filter(item => item.disabled === true).length === 0
        }

        schemaActual.resultSchema.properties.measurement_time.options = {
          ...schemaActual.resultSchema.properties.measurement_time.options,
          placeholder: loadingTimeOptions ? 'Загрузка' : 'Время замера',
          disabled: !Boolean(measurement_date) || loadingTimeOptions,
        }

        if (customers && measurement_responsible && customers.includes(measurement_responsible)) {
          delete schemaActual.resultSchema.properties.send_tg_notification
          delete schemaActual.resultSchema.properties.measurement_time
          delete schemaActual.resultSchema.properties.comment
          schemaActual.resultSchema.required = schemaActual.resultSchema.required.filter(
            item => item !== 'comment' && item !== 'measurement_time',
          )
        } else {
          delete schemaActual.resultSchema.properties.measurement_scan
          schemaActual.resultSchema.required = schemaActual.resultSchema.required.filter(
            item => item !== 'comment' && item !== 'measurement_scan',
          )
        }
      }

      return schemaActual.resultSchema || {}
    }, [schema, formData, enums, customers, disabledDates, defaultTimes, loadingTimeOptions])

    if (!isModalOpen) {
      return null
    }

    return (
      <Modal
        title={schema.title}
        onOk={handleOk}
        onCancel={handleCancel}
        open={isModalOpen}
        destroyOnHidden={true}
        okButtonProps={{ disabled: loading || loadingTimeOptions }}
      >
        {loading ? (
          <Spin />
        ) : error ? (
          <Alert message={error} type="error" showIcon />
        ) : (
          <div>
            <DefaultJsonForm
              formData={formData}
              schema={actualSchema}
              onChange={onChange}
              orientation="horizontal"
            />
          </div>
        )}
      </Modal>
    )
  },
)

MeasurementModal.displayName = 'MeasurementModal'
